import React from "react";

const MessageBubble = ({
  message,
  currentPath = [],
  onFAQSelect = null,
  onFAQClose = null,
}) => {
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderContent = () => {
    // Message rejected by the profanity filter
    if (message.isBlocked) {
      return (
        <div className="flex items-start gap-2 text-red-700">
          <span aria-hidden="true">⚠️</span>
          <p className="font-semibold">{message.content}</p>
        </div>
      );
    }

    // Handle error messages
    if (message.isError) {
      return (
        <div className="text-red-600">
          <p className="font-medium">⚠️ Error</p>
          <p>{message.content}</p>
        </div>
      );
    }

    // Handle FAQ messages with enhanced styling like image 2
    if (message.isFAQ) {
      return (
        <div>
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
            {/* FAQ Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-600">1</span>
                </div>
                <h4 className="text-lg font-bold text-gray-900">
                  {message.faqData?.question || "FAQ Question"}
                </h4>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onFAQClose && onFAQClose()}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  title="Close FAQ"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* FAQ Answer */}
            <div className="mb-4">
              <h5 className="text-sm font-bold text-gray-700 mb-2">ANSWER:</h5>
              <div className="text-sm text-gray-600 leading-relaxed">
                {message.content}
              </div>
            </div>

            {/* Related Link */}
            {message.faqData && message.faqData.url && (
              <div className="mb-4">
                <h5 className="text-sm font-bold text-gray-700 mb-2">
                  RELATED LINK:
                </h5>
                <a
                  href={message.faqData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-xl hover:bg-blue-600 transition-colors duration-200 shadow-sm"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  Visit Link
                </a>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Handle final messages with service information - Enhanced like image 3
    if (message.isFinal && message.description) {
      // Get service name from current path (last element) or use a default
      const serviceName =
        currentPath.length > 0
          ? currentPath[currentPath.length - 1]
          : "Service";

      return (
        <div className="space-y-3">
          {/* Service description */}
          <div className="whitespace-pre-wrap text-gray-800">
            {message.content}
          </div>

          {/* Enhanced Service result card like image 3 */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-bold text-green-800 mb-2">
                  Service Found
                </h4>
                {message.direct_url &&
                  message.direct_url !== "No URL available" && (
                    <div className="mt-3">
                      <a
                        href={message.direct_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors duration-200 shadow-sm"
                      >
                        <span>Open {serviceName}</span>
                        <svg
                          className="w-4 h-4 ml-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Handle separate FAQ prompt message
    if (message.isFaqPrompt) {
      return (
        <div className="space-y-3">
          <div className="whitespace-pre-wrap text-gray-800">
            {message.content}
          </div>

          {/* FAQ prompt with emoji buttons like image 3 */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">👍</span>
            <div className="flex space-x-2">
              <button className="px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-sm hover:bg-blue-200 transition-colors">
                [Yes]
              </button>
              <button className="px-3 py-1 bg-gray-100 text-gray-800 rounded-md text-sm hover:bg-gray-200 transition-colors">
                [No]
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Handle separate FAQ list message - Enhanced like image 3
    if (
      message.isFaqList &&
      message.faqResults &&
      message.faqResults.length > 0
    ) {
      return (
        <div className="space-y-4">
          <div className="whitespace-pre-wrap text-gray-800">
            {message.content}
          </div>

          {/* Instruction text */}
          <div className="text-sm text-gray-600 mb-3">
            Please select a number to see the full answer:
          </div>

          {/* FAQ results list with enhanced styling */}
          <div className="space-y-3">
            {message.faqResults.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-red-200 rounded-2xl p-4 shadow-md hover:shadow-lg transition-shadow duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-600">
                        {index + 1}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-800">
                      {faq.question}
                    </span>
                    <span className="text-gray-400">+</span>
                  </div>
                  <button
                    onClick={() => onFAQSelect && onFAQSelect(faq)}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors duration-200 shadow-sm"
                  >
                    Open
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Handle FAQ response with results - Enhanced like image 3
    if (
      message.isFaqResponse &&
      message.faqResults &&
      message.faqResults.length > 0
    ) {
      return (
        <div className="space-y-4">
          <div className="whitespace-pre-wrap text-gray-800">
            {message.content}
          </div>

          {/* Instruction text */}
          <div className="text-sm text-gray-600 mb-3">
            Please select a number to see the full answer:
          </div>

          {/* FAQ results list with enhanced styling */}
          <div className="space-y-3">
            {message.faqResults.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-red-200 rounded-2xl p-4 shadow-md hover:shadow-lg transition-shadow duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-600">
                        {index + 1}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-800">
                      {faq.question}
                    </span>
                    <span className="text-gray-400">+</span>
                  </div>
                  <button
                    onClick={() => onFAQSelect && onFAQSelect(faq)}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors duration-200 shadow-sm"
                  >
                    Open
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Handle regular messages with options - don't display options in message bubble
    if (message.options && message.options.length > 0) {
      return (
        <div>
          <div className="whitespace-pre-wrap">{message.content}</div>
        </div>
      );
    }

    // Default message content
    return <div className="whitespace-pre-wrap">{message.content}</div>;
  };

  return (
    <div className={`message ${message.type}`}>
      {/* User avatar for bot messages */}
      {message.type === "bot" && (
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
            {/* Bot avatar: robot */}
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.8}
            >
              <path strokeLinecap="round" d="M12 3v3" />
              <circle cx="12" cy="2.5" r="1" fill="currentColor" stroke="none" />
              <rect x="4" y="6" width="16" height="12" rx="4" />
              <circle cx="9.2" cy="12" r="1.3" fill="currentColor" stroke="none" />
              <circle cx="14.8" cy="12" r="1.3" fill="currentColor" stroke="none" />
              <path strokeLinecap="round" d="M9.5 15.5h5M2 10.5v3M22 10.5v3" />
            </svg>
          </div>
        </div>
      )}

      {/* Message bubble */}
      <div
        className={`message-bubble ${message.type} ${message.isFinal ? "bot-options" : ""}`}
      >
        {renderContent()}

        {/* Timestamp */}
        <div className="text-xs text-gray-500 mt-2 opacity-75">
          {formatTimestamp(message.timestamp)}
        </div>
      </div>

      {/* User avatar for user messages */}
      {message.type === "user" && (
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
            {/* User avatar: graduation cap (student) */}
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.8}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3.5L2.5 8 12 12.5 21.5 8 12 3.5z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.5 10.5v4.2c0 1.6 2.5 2.8 5.5 2.8s5.5-1.2 5.5-2.8v-4.2"
              />
              <path strokeLinecap="round" d="M21.5 8v4.5" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageBubble;
